import { ForwardedRef, forwardRef, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Merge } from "src/utils/types";

export interface CoreInputProps {
  id: string;
  label?: string;
  icon?: ReactNode;
  className?: string | undefined;
  labelClassName?: string | undefined;
  type?:
    | "text"
    | "email"
    | "password"
    | "checkbox"
    | "number"
    | "boolean"
    | "radio"
    | "button";
  variant?: "standard" | "primary" | "secondary";
  error?: string;
  touched?: boolean;
}

export type InputProps = Merge<React.ComponentProps<"input">, CoreInputProps>;

export const Input = forwardRef(function Input(
  props: InputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  function CoreLabel() {
    return (
      <label
        htmlFor={id}
        className={twMerge(
          "text-black font-bold m-0 ml-0.5 mb-0.5",
          type === "checkbox" &&
            "font-semibold float-right text-sm ml-2 py-0.5",
          type === "radio" && "text-md",
          error && "text-red",
          labelClassName,
        )}
      >
        {label}
      </label>
    );
  }

  function CoreIcon() {
    return (
      <div
        className={twMerge(
          "absolute top-1.5 left-1",
          variant === "standard" && "text-gray-800",
          error && "text-red",
        )}
      >
        {icon}
      </div>
    );
  }

  const {
    id,
    label,
    icon,
    className,
    labelClassName,
    type = "text",
    error = false,
    touched = false,
    variant,
    ...inputProps
  } = props;

  return (
    <div className="text-left">
      <div
        className={twMerge(
          "relative",
          (type === "checkbox" || type == "radio") &&
            "flex align-middle flex-row-reverse justify-end",
        )}
      >
        {icon && (
          <div>
            <CoreIcon />
          </div>
        )}
        {label && (
          <div>
            <CoreLabel />
          </div>
        )}
        <input
          ref={ref}
          {...inputProps}
          value={inputProps.value}
          className={twMerge(
            "w-full p-2 placeholder:text-lg text-lg",
            icon && "pl-8",
            variant === "standard" &&
              "text-black border-0 border-b border-b-black focus:ring-0 focus:border-b-blue",
            variant === "primary" &&
              "bg-white border border-gray-600 text-black focus:border-blue focus:border-2 focus:ring-blue",
            variant === "secondary" &&
              "bg-gray-400 border border-gray-600 text-black focus:border-blue focus:border-2 focus:ring-blue",
            variant === "primary" &&
              type === "checkbox" &&
              "w-4 h-4 rounded text-blue checked:bg-blue",
            variant === "primary" &&
              type === "radio" &&
              "float-left mt-1 mr-1 w-2.5 h-2.5 rounded-full text-blue checked:bg-blue",
            error &&
              variant === "standard" &&
              "border-red text-red placeholder:text-red",
            error &&
              variant === "primary" &&
              "focus:border-red focus:border-2 focus:ring-red border-2 border-red text-red",
            className,
          )}
          type={type}
        />
      </div>
    </div>
  );
});

export default Input;
